/** @file Card Acceptance section */

import React, { useState } from 'react';
import { Box, FormControlLabel, Checkbox, Typography } from '@mui/material';
import { testId as parentTestId } from '../util';
import { CardBrand } from '../../../util/constants';
import {
  CARD_ACCEPTANCE_MIN_ONE_HINT,
  CARD_ACCEPTANCE_NETWORKS,
  hasAtLeastOneAcceptedCardNetwork,
} from '../../../util/cardAcceptance';

export const testId = `${parentTestId}-CARD_ACCEPTANCE`;

type Props = {
  nonAcceptedCardBrands?: CardBrand[];
  onNonAcceptedCardBrandsChange: (nonAcceptedCardBrands: CardBrand[]) => void;
  disabled?: boolean;
};

const NETWORK_LABELS: Record<CardBrand, string> = {
  amex: 'American Express',
  mastercard: 'Mastercard',
  visa: 'Visa',
  discover: 'Discover',
};

/** Missing config or empty list = all networks accepted (card-linking only filters when list is non-empty). */
const getCheckboxValues = (nonAcceptedCardBrands: CardBrand[] | undefined) => {
  const rejectedBrands = nonAcceptedCardBrands ?? [];
  const checkboxValues: Record<CardBrand, boolean> = {
    amex: false,
    discover: false,
    mastercard: false,
    visa: false,
  };
  Object.keys(checkboxValues).forEach((cardType) => {
    checkboxValues[cardType as CardBrand] = !rejectedBrands.includes(cardType as CardBrand);
  });
  return checkboxValues;
};

const CardAcceptance: React.FC<Props> = ({
  nonAcceptedCardBrands,
  onNonAcceptedCardBrandsChange,
  disabled,
}: Props) => {
  const defaultCheckboxValues = getCheckboxValues(nonAcceptedCardBrands);
  const [checkboxValues, setCheckboxValues] = useState(defaultCheckboxValues);

  const handleCheckboxChange = (brand: CardBrand) => {
    const newCheckboxValues: Record<CardBrand, boolean> = {
      ...checkboxValues,
      [brand]: !checkboxValues[brand],
    };

    const nextRejected = CARD_ACCEPTANCE_NETWORKS.filter(
      (cardType) => !newCheckboxValues[cardType],
    );

    if (!hasAtLeastOneAcceptedCardNetwork(nextRejected)) {
      return;
    }

    onNonAcceptedCardBrandsChange(nextRejected);
    setCheckboxValues(newCheckboxValues);
  };

  return (
    <Box component="section" data-testid={testId}>
      <Typography variant="large" fontWeight="bold" sx={{ mb: 1 }}>
        Card Acceptance
      </Typography>
      <Typography variant="small" color="text.secondary">
        Please select which networks you accept. Card networks that are not accepted will be hidden
        from customers.
      </Typography>
      <Box sx={{ my: 2 }}>
        <Typography variant="small" fontWeight="semibold">
          Accepted networks
        </Typography>
        <Typography variant="small" color="text.secondary" sx={{ mt: 0.5 }}>
          {CARD_ACCEPTANCE_MIN_ONE_HINT}
        </Typography>
      </Box>
      {CARD_ACCEPTANCE_NETWORKS.map((brand) => (
        <Box key={brand}>
          <FormControlLabel
            sx={{
              '& .MuiFormControlLabel-label': { ml: 1 },
              '& .MuiCheckbox-root': {
                py: 0.5,
                pr: 1,
                pl: 0,
              },
              ml: 0,
            }}
            control={
              <Checkbox
                checked={checkboxValues[brand]}
                onChange={() => handleCheckboxChange(brand)}
                disabled={disabled}
              />
            }
            data-testid={`${testId}-${brand}`}
            label={NETWORK_LABELS[brand]}
            id={brand}
          />
        </Box>
      ))}
    </Box>
  );
};

export default CardAcceptance;
